import { superAdminApi } from "../authService";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../../types/api";

const RESOURCE = "/super-admin/accounts";

export type AccountMembershipStatus =
  | "pending"
  | "active"
  | "suspended"
  | "revoked";

export interface AccountMembership {
  id: number;
  tenant_id: number;
  tenant_name: string | null;
  user_id: number | null;
  status: AccountMembershipStatus;
  roles: string[];
  invited_at: string | null;
  accepted_at: string | null;
  revoked_at: string | null;
  last_accessed_at: string | null;
}

export interface AccountSocial {
  id: number;
  provider: string;
  email: string | null;
  provider_user_id: string;
}

export interface AdatisAccount {
  id: number;
  email: string;
  name: string | null;
  locale: string | null;
  email_verified_at: string | null;
  has_password: boolean;
  google_linked: boolean;
  google_email?: string | null;
  is_orphan: boolean;
  memberships_count: number;
  created_at: string | null;
  updated_at: string | null;
  memberships?: AccountMembership[];
  social_accounts?: AccountSocial[];
}

export interface AccountListParams extends PaginationParams {
  search?: string;
  orphan?: boolean;
}

export const getAccounts = async (
  params?: AccountListParams
): Promise<PaginatedApiResponse<AdatisAccount>> => {
  const response = await superAdminApi.get<PaginatedApiResponse<AdatisAccount>>(
    RESOURCE,
    { params }
  );
  return response.data;
};

export const getAccount = async (
  id: number
): Promise<ApiResponse<AdatisAccount>> => {
  const response = await superAdminApi.get<ApiResponse<AdatisAccount>>(
    `${RESOURCE}/${id}`
  );
  return response.data;
};

export const deleteAccount = async (
  id: number,
  confirmPhrase?: string
): Promise<ApiResponse<{ id: number; email: string; deleted: boolean; was_orphan: boolean }>> => {
  const response = await superAdminApi.delete<
    ApiResponse<{ id: number; email: string; deleted: boolean; was_orphan: boolean }>
  >(`${RESOURCE}/${id}`, {
    data: confirmPhrase ? { confirm_phrase: confirmPhrase } : undefined,
  });
  return response.data;
};

export const revokeAccountGoogle = async (
  id: number
): Promise<ApiResponse<AdatisAccount>> => {
  const response = await superAdminApi.post<ApiResponse<AdatisAccount>>(
    `${RESOURCE}/${id}/revoke-google`
  );
  return response.data;
};

export const invalidateAccountTokens = async (
  id: number
): Promise<
  ApiResponse<{
    identity_id: number;
    identity_tokens_revoked: number;
    refresh_tokens_revoked: number;
  }>
> => {
  const response = await superAdminApi.post<
    ApiResponse<{
      identity_id: number;
      identity_tokens_revoked: number;
      refresh_tokens_revoked: number;
    }>
  >(`${RESOURCE}/${id}/invalidate-tokens`);
  return response.data;
};

export const downloadOrphanAccountsCsv = async (params?: {
  search?: string;
}): Promise<Blob> => {
  const response = await superAdminApi.get(`${RESOURCE}/export.csv`, {
    params,
    responseType: "blob",
  });
  return response.data;
};

export const batchDeleteOrphanAccounts = async (
  ids: number[],
  confirmPhrase: string
): Promise<
  ApiResponse<{ deleted_count: number; deleted_ids: number[] }>
> => {
  const response = await superAdminApi.post<
    ApiResponse<{ deleted_count: number; deleted_ids: number[] }>
  >(`${RESOURCE}/batch-delete`, {
    ids,
    confirm_phrase: confirmPhrase,
  });
  return response.data;
};
